import React, { useState, useRef, useCallback, useEffect } from 'react';

/**
 * SentMailFrame — renders raw email HTML in an isolated, same-origin `srcDoc`
 * iframe that auto-sizes to its content.
 *
 * Used for our OWN sent mail in the in-app inbox so the preview is a 1:1 copy
 * of what actually left the server:
 *   - The footer image loads directly (no broken /api/proxy-image rewrite).
 *   - The app's global `* { margin: 0 }` reset can't leak in and squash the
 *     footer spacing.
 * Both reasons are why the react-letter path misrenders sent mail.
 */
export default function SentMailFrame({ html, title, minHeight = 400 }) {
  const [height, setHeight] = useState(minHeight);
  const frameRef = useRef(null);
  const timers = useRef([]);

  useEffect(() => () => {
    timers.current.forEach(clearTimeout);
  }, []);

  const measure = useCallback(() => {
    try {
      const doc = frameRef.current && frameRef.current.contentDocument;
      if (doc && doc.documentElement) {
        const h = Math.max(
          doc.body ? doc.body.scrollHeight : 0,
          doc.documentElement.scrollHeight
        );
        if (h > 0) setHeight(h + 16);
      }
    } catch (_) {
      /* same-origin read failed — keep current height */
    }
  }, []);

  const handleLoad = useCallback(() => {
    measure();
    // Footer logos use `height:auto`, so their box height is only known once the
    // image bytes arrive — re-measure a couple of times after load.
    timers.current.forEach(clearTimeout);
    timers.current = [window.setTimeout(measure, 250), window.setTimeout(measure, 900)];
  }, [measure]);

  const srcDoc = `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"><base target="_blank"><style>html,body{margin:0;padding:0;background:#fff;}body{padding:4px;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Helvetica,Arial,sans-serif;color:#333;}img{max-width:100%;}</style></head><body>${html || ''}</body></html>`;

  return (
    <iframe
      ref={frameRef}
      title={title || 'Sent email'}
      className="sent-mail-frame"
      srcDoc={srcDoc}
      onLoad={handleLoad}
      style={{ width: '100%', height, border: 'none', background: '#fff' }}
      sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
    />
  );
}
